import { useCallback, useEffect, useRef, useState } from 'react';
import { getContainer } from '@framework/di/getContainer';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import { getErrorMessage } from '@framework/message/Mapper/getMessage';
import AskAiChatRepository from '@/creditflowresearch/infrastructure/repository/AskAiChatRepository';
import { IAskAiChatSummary } from '@/creditflowresearch/domain/vo/AskAiChat';
import { CHATS_PER_PAGE } from '../constants';

interface IParams {
  isEnabled: boolean,
}

const useAskAiChatHistory = ({ isEnabled }: IParams) => {
  const [chats, setChats] = useState<IAskAiChatSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isFetching, setIsFetching] = useState(false);
  const requestIdRef = useRef(0);

  const repository = getContainer().get<AskAiChatRepository>(AskAiChatRepository);

  const fetchPage = useCallback(async (nextPage: number): Promise<void> => {
    const requestId = ++requestIdRef.current;
    const isLatest = (): boolean => requestId === requestIdRef.current;

    setIsFetching(true);

    try {
      const result = await repository.getChats(nextPage, CHATS_PER_PAGE);

      if (!isLatest()) return;

      setTotal(result.total);
      setPage(result.page);
      setChats((prev) => (nextPage === 1 ? result.items : [...prev, ...result.items]));
    } catch (error) {
      if (!isLatest()) return;

      AlertManager.error(getErrorMessage(error));
    } finally {
      if (isLatest()) {
        setIsFetching(false);
      }
    }
  }, [repository]);

  const refresh = useCallback((): Promise<void> => fetchPage(1), [fetchPage]);

  const loadMore = useCallback((): Promise<void> | undefined => {
    if (isFetching || chats.length >= total) return undefined;

    return fetchPage(page + 1);
  }, [fetchPage, isFetching, chats.length, total, page]);

  const remove = useCallback(async (chatId: string): Promise<boolean> => {
    const previousChats = chats;
    const previousTotal = total;

    setChats((prev) => prev.filter((chat) => chat.id !== chatId));
    setTotal((prev) => Math.max(prev - 1, 0));

    try {
      await repository.deleteChat(chatId);

      return true;
    } catch (error) {
      setChats(previousChats);
      setTotal(previousTotal);
      AlertManager.error(getErrorMessage(error));

      return false;
    }
  }, [chats, total, repository]);

  useEffect(() => {
    if (isEnabled) refresh();
  }, [isEnabled, refresh]);

  return {
    chats,
    isFetching,
    hasMore: chats.length < total,
    refresh,
    loadMore,
    remove,
  };
};

export default useAskAiChatHistory;
